"use client";

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { supabase } from '../lib/supabaseClient';
import { useAllProducts } from '../context/ProductContext';
import ProductGrid from './ProductGrid';
import { Product, SalesPageType } from '../types';
import { sortProductsByNewest } from '../utils/productUtils';
import { productHasTag } from '../utils/productTags';

interface SalesClientProps {
  initialConfig?: SalesPageType | null;
  initialProducts?: Product[];
  slug?: string;
}

export default function SalesClient({ initialConfig, initialProducts, slug: propSlug }: SalesClientProps) {
    const params = useParams();
    const slug = propSlug || (params?.slug as string | undefined);

    const [config, setConfig] = useState<SalesPageType | null>(initialConfig ?? null);
    const [loading, setLoading] = useState<boolean>(!initialConfig);
    const { allProducts, isLoading: productsLoading, loadProducts } = useAllProducts();

    useEffect(() => {
        void loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        if (initialConfig) {
            setConfig(initialConfig);
            setLoading(false);
            return;
        }

        const fetchSalesPage = async () => {
            if (!slug) return;
            const cleanSlug = slug.trim();
            setLoading(true);
            try {
                const res = await fetch(`/api/sale/${encodeURIComponent(cleanSlug)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data && !data.error) {
                        setConfig(data);
                        return;
                    }
                }

                const { data, error } = await supabase
                    .from('sales_pages')
                    .select('*')
                    .eq('slug', cleanSlug)
                    .eq('is_active', true)
                    .maybeSingle();

                if (error) throw error;
                setConfig(data);
            } catch (err) {
                console.error("Error fetching sales page:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchSalesPage();
    }, [slug, initialConfig]);

    const filteredProducts = useMemo(() => {
        if (!config) return [];

        if (allProducts && allProducts.length > 0) {
            if (config.filter_tags && config.filter_tags.length > 0) {
                return sortProductsByNewest(allProducts.filter(product =>
                    config.filter_tags.some(tag => productHasTag(product.tags, tag, product.name, product.slug))
                ));
            }
            return sortProductsByNewest(allProducts);
        }

        if (initialProducts && initialProducts.length > 0) {
            return initialProducts;
        }

        return [];
    }, [config, allProducts, initialProducts]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
                <h2 className="text-xl font-bold text-accent mb-3">Sale Page Not Found</h2>
                <p className="text-gray-500 mb-6 text-sm">This sale might have ended or the link is incorrect.</p>
                <Link prefetch={false} href="/products" className="bg-primary text-white px-6 py-2.5 rounded-full font-medium text-sm">
                    Shop All Products
                </Link>
            </div>
        );
    }

    return (
        <div className="pb-16 bg-background">
            {/* Clean Minimalist Sales Banner */}
            <div className="relative w-full h-[180px] xs:h-[220px] sm:h-[280px] md:h-[360px] bg-slate-900 overflow-hidden">
                <img
                    src={config.banner_image}
                    alt={config.title}
                    className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />

                <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-8 md:p-12 max-w-7xl mx-auto w-full">
                    <div className="max-w-2xl">
                        <span className="inline-block text-[11px] sm:text-xs font-semibold text-amber-400 tracking-wider uppercase mb-1">
                            Special Sale
                        </span>
                        <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-1.5">
                            {config.title}
                        </h1>
                        {config.subtitle && (
                            <p className="text-xs sm:text-base text-slate-200 font-normal leading-relaxed">
                                {config.subtitle}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Product Section Header & Grid Container */}
            <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pt-8">
                <div className="flex items-center justify-between gap-4 mb-6 pb-3 border-b border-gray-100">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                        Products ({filteredProducts.length})
                    </h2>
                </div>

                <div>
                    {productsLoading && filteredProducts.length === 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="aspect-[3/4] bg-gray-100 rounded-xl animate-pulse" />
                            ))}
                        </div>
                    ) : filteredProducts.length > 0 ? (
                        <ProductGrid products={filteredProducts} loading={false} />
                    ) : (
                        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                            <p className="text-gray-500 text-sm">No products found for this sale.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
